import { ProductTypeName } from '@electricjs/core_entity-client';
import { Flex, spaces } from '@electricjs/arc';

import {
  type DynamicFilters,
  StorefrontProductTypeGroup,
  DynamicFilterKey,
} from '@/types/storefront';
import {
  PRODUCT_TYPES_BY_TYPE_GROUP,
  TYPE_GROUPS_WITH_SPECIAL_TYPE_FILTER,
} from '@/constants/storefront';

import { DynamicFilterDropdown } from './DynamicFilterDropdown';
import { SelectedOptionChipList } from './SelectedOptionChipList';

type DynamicFiltersGroupProps = {
  isAvailableDynamicFiltersLoading?: boolean;
  availableDynamicFilters?: DynamicFilters;
  dynamicFiltersSelectedValues?: DynamicFilters;
  onDynamicFiltersValuesSelect: (dynamicFiltersValues: DynamicFilters) => void;
  selectedProductTypeGroup: StorefrontProductTypeGroup;
  /** when a product type is changed by the special "TYPE" dynamic filter (e.g, for keyboard/mouse/combo) */
  onProductTypesSelect: (types: ProductTypeName[]) => void;
};

export const DynamicFiltersGroup = ({
  isAvailableDynamicFiltersLoading,
  availableDynamicFilters,
  dynamicFiltersSelectedValues,
  onDynamicFiltersValuesSelect,
  selectedProductTypeGroup,
  onProductTypesSelect,
}: DynamicFiltersGroupProps) => {
  const handleOptionsSelect = (
    newSelectedOptions: string[],
    filterKey: DynamicFilterKey
  ) => {
    // prepare the new dynamic filters values
    const newDynamicFiltersSelectedValues = {
      ...dynamicFiltersSelectedValues,
      [filterKey]: newSelectedOptions,
    };

    if (newDynamicFiltersSelectedValues[filterKey]?.length === 0) {
      delete newDynamicFiltersSelectedValues[filterKey];
    }

    // update the dynamic filters values
    onDynamicFiltersValuesSelect(newDynamicFiltersSelectedValues);

    // also, update the product types (when the special "TYPE" filter is used)
    if (filterKey === DynamicFilterKey.TYPE) {
      if (newSelectedOptions.length === 0) {
        // when no type is selected, return all types of this group
        onProductTypesSelect(
          PRODUCT_TYPES_BY_TYPE_GROUP[selectedProductTypeGroup]
        );
      } else {
        onProductTypesSelect([...newSelectedOptions] as ProductTypeName[]);
      }
    }
  };

  const handleOptionRemove = (
    filterOption: string,
    filterKey: DynamicFilterKey
  ) => {
    // prepare the new selected options list (with the option removed)
    const newSelectedOptions = dynamicFiltersSelectedValues?.[
      filterKey
    ]?.filter(option => option !== filterOption);

    // update the options
    handleOptionsSelect(newSelectedOptions ?? [], filterKey);
  };

  const handleClearAllFilters = () => {
    // reset the dynamic filters
    onDynamicFiltersValuesSelect({});

    // also, reset the product types (when the special "TYPE" filter is available)
    if (allAvailableFilters?.product_type) {
      // reset the product types to be the ones of that group
      onProductTypesSelect(
        PRODUCT_TYPES_BY_TYPE_GROUP[selectedProductTypeGroup]
      );
    }
  };

  const getAllAvailableFilters = (backendAvailableFilters?: DynamicFilters) => {
    // when the selected type group is configured to have the special "TYPE" filter, the frontend adds it in the end
    if (
      TYPE_GROUPS_WITH_SPECIAL_TYPE_FILTER.includes(selectedProductTypeGroup)
    ) {
      return {
        [DynamicFilterKey.TYPE]:
          PRODUCT_TYPES_BY_TYPE_GROUP[selectedProductTypeGroup], // the options are the product types of that group
        ...backendAvailableFilters,
      };
    }

    return backendAvailableFilters;
  };

  const allAvailableFilters = getAllAvailableFilters(availableDynamicFilters);

  return (
    <Flex
      vertical
      rowGap={spaces.space_m}
      width="100%"
      testId="dynamic-filters">
      {isAvailableDynamicFiltersLoading && (
        // just a fake filter, in the loading state
        <DynamicFilterDropdown
          loading={true}
          filterKey={DynamicFilterKey.MANUFACTURER} // Any filter key. It won't be really used in this case.
          availableOptions={[]}
          selectedOptions={[]}
          onSelect={() => {}}
        />
      )}

      {!isAvailableDynamicFiltersLoading && allAvailableFilters && (
        <Flex wrap columnGap="2rem" rowGap={spaces.space_s}>
          {(Object.keys(allAvailableFilters) as DynamicFilterKey[]).map(
            filterKey => {
              return (
                <DynamicFilterDropdown
                  key={filterKey}
                  filterKey={filterKey}
                  availableOptions={allAvailableFilters[filterKey] ?? []}
                  selectedOptions={
                    dynamicFiltersSelectedValues?.[filterKey] ?? []
                  }
                  onSelect={handleOptionsSelect}
                />
              );
            }
          )}
        </Flex>
      )}

      <SelectedOptionChipList
        dynamicFiltersSelectedValues={dynamicFiltersSelectedValues}
        onOptionRemove={handleOptionRemove}
        onClearAllFilters={handleClearAllFilters}
      />
    </Flex>
  );
};
